import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreType } from '../../reducers';
import { useTriggeredHint } from '../../utils/useTriggeredHint';
import { ampliTrackMessageDisplayedEvent, MessageType } from '../../services/track.service';
import { safeSessionStorage } from '../../utils/safeStorage';
import { spaces } from '../../assets/styles/themes/tokens';
import PromoBanner from './PromoBanner';
import { getPromoCampaign } from './promoCampaigns';

export interface DashboardPromoBannerProps {
  // Resolved by the dashboard: true for the `dashboard` experiment treatment
  // cohort (AI-eligible). Riding that gate also guarantees the `/ki-tools` CTA
  // target is available to the viewer.
  isEligible: boolean;
  // Optional observation seams. `onShow` fires alongside the impression event;
  // `onCtaClick` is the hook for a dedicated CTA-click event (PRO-9).
  onShow?: (campaignId: string) => void;
  onCtaClick?: (campaignId: string) => void;
}

const ACTIVE_CAMPAIGN_ID = 'podcast-launch';

const DashboardPromoBanner: React.FC<DashboardPromoBannerProps> = ({ isEligible, onShow, onCtaClick }) => {
  const { t } = useTranslation();
  const isUserLoaded = useSelector((state: StoreType) => Boolean(state.user.me));

  const campaign = isEligible ? getPromoCampaign(ACTIVE_CAMPAIGN_ID) : undefined;

  // Server-persisted dismissal, keyed per campaign so a new campaign shows even
  // to users who dismissed a previous one.
  const { hasSeen, record } = useTriggeredHint(`promo-banner-${campaign?.id ?? 'none'}`);
  const [dismissed, setDismissed] = useState(false);

  const shouldShow = isUserLoaded && Boolean(campaign) && !hasSeen && !dismissed;

  const hasFiredOnShow = useRef(false);
  useEffect(() => {
    if (hasFiredOnShow.current || !shouldShow || !campaign) {
      return;
    }
    hasFiredOnShow.current = true;
    onShow?.(campaign.id);

    // Impression fires at most once per session so re-mounting the dashboard
    // (navigating away and back) doesn't inflate the count.
    const impressionKey = `promo-banner-impression-${campaign.id}`;
    if (!safeSessionStorage.getItem(impressionKey)) {
      safeSessionStorage.setItem(impressionKey, 'true');
      ampliTrackMessageDisplayedEvent(campaign.trackingCampaign, MessageType.BANNER);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow]);

  if (!shouldShow || !campaign) {
    return null;
  }

  const dismiss = () => {
    record();
    setDismissed(true);
  };

  return (
    <PromoBanner
      headline={t(campaign.headlineKey)}
      body={t(campaign.bodyKey)}
      ctaLabel={t(campaign.ctaKey)}
      ctaHref={campaign.ctaRoute}
      onCtaClick={() => onCtaClick?.(campaign.id)}
      secondaryLabel={campaign.secondaryKey ? t(campaign.secondaryKey) : undefined}
      secondaryHref={campaign.secondaryRoute}
      onDismiss={dismiss}
      sx={{ marginRight: spaces.md }}
    />
  );
};

export default DashboardPromoBanner;
